/**
 * API Utilities for Catalog Imports
 * 
 * Provides consistent error handling including:
 * - 401 Unauthorized detection with toast notification and redirect to login
 * - User-friendly error messages
 * - Cookie-based session authentication
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Track if we're already handling a 401 to prevent multiple redirects
let isHandling401 = false;

/**
 * Get default headers for API requests
 * Note: Authentication is handled via cookies, not Authorization header
 */
export const getAuthHeaders = () => {
  return {
    'Content-Type': 'application/json'
  };
};

/**
 * Error class for API errors with status code
 */
export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }

  get isUnauthorized() {
    return this.status === 401;
  }

  get isForbidden() {
    return this.status === 403;
  }

  get isNotFound() {
    return this.status === 404;
  }

  get isServerError() {
    return this.status >= 500;
  }
}

/**
 * Show session expired notification
 * Uses a custom toast-like element for visibility
 */
const showSessionExpiredToast = () => {
  // Check if toast already exists
  if (document.getElementById('session-expired-toast')) return;
  
  // Create toast element
  const toast = document.createElement('div');
  toast.id = 'session-expired-toast';
  toast.setAttribute('data-testid', 'session-expired-toast');
  toast.style.cssText = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: #dc2626;
    color: white;
    padding: 16px 24px;
    border-radius: 8px;
    box-shadow: 0 10px 25px rgba(0,0,0,0.2);
    z-index: 99999;
    font-family: system-ui, -apple-system, sans-serif;
    font-size: 14px;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 12px;
    animation: slideDown 0.3s ease-out;
  `;
  
  // Add animation keyframes
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideDown {
      from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
      to { opacity: 1; transform: translateX(-50%) translateY(0); }
    }
  `;
  document.head.appendChild(style);
  
  toast.innerHTML = `
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
    <span>Session expired. Redirecting to login...</span>
  `;
  
  document.body.appendChild(toast);
};

/**
 * Handle 401 errors by showing toast and redirecting to login
 */
const handle401 = () => {
  // Prevent multiple redirects
  if (isHandling401) return;
  isHandling401 = true;
  
  // Show toast notification
  showSessionExpiredToast();
  
  // Redirect to login after brief delay (allows toast to be seen)
  setTimeout(() => {
    const returnUrl = encodeURIComponent(window.location.pathname);
    window.location.href = `/login?redirect=${returnUrl}`;
  }, 1500);
};

/**
 * Reset 401 handling state (useful for testing)
 */
export const reset401State = () => {
  isHandling401 = false;
  const toast = document.getElementById('session-expired-toast');
  if (toast) toast.remove();
};

/**
 * Make an authenticated API request with proper error handling
 * 
 * @param {string} endpoint - API endpoint (e.g., '/catalog-imports/admin/vendors')
 * @param {object} options - Fetch options
 * @returns {Promise<any>} - Response data
 * @throws {ApiError} - On API errors
 */
export const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_URL}/api${endpoint}`;
  
  const config = {
    ...options,
    credentials: 'include',  // Send cookies with request for auth
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  };

  // Remove Content-Type for FormData (let browser set it)
  if (options.body instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  try {
    const response = await fetch(url, config);
    
    // Handle 401 - Session expired
    if (response.status === 401) {
      handle401();
      throw new ApiError(
        'Your session has expired. Please log in again.',
        401
      );
    }

    // Handle 403 - Forbidden
    if (response.status === 403) {
      const data = await response.json().catch(() => ({}));
      throw new ApiError(
        data.detail || 'You do not have permission to perform this action.',
        403,
        data
      );
    }

    // Handle 404 - Not found
    if (response.status === 404) {
      const data = await response.json().catch(() => ({}));
      throw new ApiError(
        data.detail || 'The requested resource was not found.',
        404,
        data
      );
    }

    // Handle other errors
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new ApiError(
        data.detail || `Request failed with status ${response.status}`,
        response.status,
        data
      );
    }

    // Return JSON response
    return await response.json();
  } catch (error) {
    // Re-throw ApiError as-is
    if (error instanceof ApiError) {
      throw error;
    }
    
    // Network errors
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      throw new ApiError(
        'Unable to connect to the server. Please check your internet connection.',
        0
      );
    }
    
    // Other errors
    throw new ApiError(error.message || 'An unexpected error occurred', 0);
  }
};

/**
 * GET request helper
 */
export const apiGet = (endpoint, params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value);
    }
  });
  
  const queryString = searchParams.toString();
  const url = queryString ? `${endpoint}?${queryString}` : endpoint;
  
  return apiRequest(url, { method: 'GET' });
};

/**
 * POST request helper
 */
export const apiPost = (endpoint, data) => {
  return apiRequest(endpoint, {
    method: 'POST',
    body: data instanceof FormData ? data : JSON.stringify(data)
  });
};

/**
 * PATCH request helper
 */
export const apiPatch = (endpoint, data) => {
  return apiRequest(endpoint, {
    method: 'PATCH',
    body: JSON.stringify(data)
  });
};

/**
 * DELETE request helper
 */
export const apiDelete = (endpoint) => {
  return apiRequest(endpoint, { method: 'DELETE' });
};

/**
 * Format error message for display
 */
export const formatErrorMessage = (error) => {
  if (error instanceof ApiError) {
    if (error.isUnauthorized) {
      return 'Session expired. Redirecting to login...';
    }
    if (error.isForbidden) {
      return 'Access denied. You do not have permission for this action.';
    }
    if (error.isNotFound) {
      return 'Resource not found.';
    }
    if (error.isServerError) {
      return 'Server error. Please try again later.';
    }
    return error.message;
  }
  return error?.message || 'An unexpected error occurred';
};

export default {
  apiRequest,
  apiGet,
  apiPost,
  apiPatch,
  apiDelete,
  getAuthHeaders,
  formatErrorMessage,
  ApiError
};
